// File: src/blueprint/components/content/question/QuestionContent.jsx

/**
 * GOAL:
 * - Render question steps (single/multi/text/rank).
 * - IMPORTANT UX:
 *   - text/textarea answers are only committed to global state on blur (focus loss),
 *     so progressive insights won't fire on every keystroke.
 *
 * @author GoViral Web
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS } from '../../state/blueprintReducer.js';
import { selectActiveModule, selectActiveTrackId } from '../../state/selectors.js';
import { useBlueprintState } from '../../state/useBlueprintState.js';

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function normalizeChoices(item) {
  const raw = asArray(item?.choices);

  return raw
    .map((c, idx) => {
      if (!c || typeof c !== 'object') return null;

      const id = c?.id != null ? String(c.id).trim() : '';
      const label = c?.label != null ? String(c.label) : '';
      const explanation = c?.explanation != null ? String(c.explanation) : '';
      const safeLabel = label || id || `Option ${idx + 1}`;
      const key = id || safeLabel;

      return { id: key, label: safeLabel, explanation };
    })
    .filter(Boolean);
}

function shouldUseTextarea(item) {
  const ui = item?.ui && typeof item.ui === 'object' ? item.ui : null;

  if (ui?.input === 'textarea') return true;
  if (ui?.type === 'textarea') return true;
  if (ui?.multiline === true) return true;
  if (item?.multiline === true) return true;
  if (item?.textarea === true) return true;

  const rows = Number(ui?.rows ?? item?.rows ?? 0);
  return rows >= 2;
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  if (!p || !m || !t || !s) return s;

  return `${p}::${m}::${t}::${s}`;
}

export default function QuestionContent({ item }) {
  const { state, dispatch } = useBlueprintState();

  const packId = String(state?.packId || 'none').trim();
  const module = selectActiveModule(state);
  const activeTrackId = selectActiveTrackId(state);
  const trackId = activeTrackId || module?.default_track_id || 'quick';

  const stepId = String(item?.id ?? '').trim();
  const answerKey = makeAnswerKey(packId, module?.id, trackId, stepId);

  const type = item?.inputType ? String(item.inputType) : item?.type ? String(item.type) : '';

  const title = item?.title ? String(item.title) : 'Question';
  const explainQuestion = item?.explanation ? String(item.explanation) : '';
  const instruction = item?.instruction ? String(item.instruction) : '';

  const isText = type === 'text';
  const useTextarea = isText && shouldUseTextarea(item);
  const isTextareaType = type === 'textarea';
  const isTextLike = isText || isTextareaType;

  const allAnswers = state?.answers && typeof state.answers === 'object' ? state.answers : {};
  const explanationMode = Boolean(state?.explanationMode);
  const committedValueRaw = allAnswers[answerKey];

  const committedTextValue = isTextLike
    ? typeof committedValueRaw === 'string'
      ? committedValueRaw
      : committedValueRaw != null
        ? String(committedValueRaw)
        : ''
    : '';

  const [draft, setDraft] = useState(isTextLike ? committedTextValue : '');
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isTextLike) return;
    setDraft(committedTextValue);
    isFocusedRef.current = false;
  }, [isTextLike, committedTextValue, stepId, answerKey]);

  useEffect(() => {
    if (!isTextLike) return;
    if (isFocusedRef.current) return;
    setDraft(committedTextValue);
  }, [isTextLike, committedTextValue]);

  function dispatchAnswer(value) {
    if (!stepId) return;

    dispatch({
      type: ACTIONS.SET_ANSWER,
      payload: {
        stepId,
        answerKey,
        packId,
        moduleId: module?.id || '',
        trackId,
        value,
      },
    });
  }

  function commitDraft(value) {
    const v = typeof value === 'string' ? value : String(value ?? '');
    dispatchAnswer(v.trim());
  }

  const choices = useMemo(() => normalizeChoices(item), [item]);

  const multiple = type === 'multi';
  const min = Number(item?.min ?? (multiple ? 1 : 1)) || (multiple ? 1 : 1);
  const max =
    Number(item?.max ?? (multiple ? choices.length : 1)) || (multiple ? choices.length : 1);

  const selectedKeys = useMemo(() => {
    if (!committedValueRaw) return [];
    if (Array.isArray(committedValueRaw)) return committedValueRaw.map(String);
    return [String(committedValueRaw)];
  }, [committedValueRaw]);

  function isSelected(key) {
    return selectedKeys.includes(String(key));
  }

  function toggleExplanationMode() {
    dispatch({
      type: ACTIONS.TOGGLE_EXPLANATION_MODE,
      payload: {},
    });
  }

  function toggleChoice(key) {
    const k = String(key);
    if (!stepId) return;

    if (!multiple) {
      dispatchAnswer(k);
      return;
    }

    const next = selectedKeys.slice();

    if (next.includes(k)) {
      dispatchAnswer(next.filter((x) => x !== k));
      return;
    }

    if (next.length >= max) return;

    next.push(k);
    dispatchAnswer(next);
  }

  if (isText || isTextareaType) {
    const ui = item?.ui && typeof item.ui === 'object' ? item.ui : {};
    const placeholder =
      typeof ui?.placeholder === 'string'
        ? ui.placeholder
        : typeof item?.placeholder === 'string'
          ? item.placeholder
          : '';

    const renderTextarea = isTextareaType || useTextarea;
    const rows = Math.max(2, Number(ui?.rows ?? item?.rows ?? (renderTextarea ? 4 : 0)) || 0);

    return (
      <div>
        <p className="dv-br-q__question">{title}</p>
        {instruction ? <p className="dv-br-card__text dv-br-card__muted">{instruction}</p> : null}

        <div style={{ marginTop: 16 }}>
          {renderTextarea ? (
            <textarea
              className="dv-br-q__textInput"
              placeholder={placeholder}
              rows={rows}
              value={draft}
              onChange={(e) => setDraft(String(e?.target?.value ?? ''))}
              onFocus={() => {
                isFocusedRef.current = true;
              }}
              onBlur={(e) => {
                isFocusedRef.current = false;
                commitDraft(String(e?.target?.value ?? ''));
              }}
              style={{
                width: '100%',
                borderRadius: 14,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.18)',
                background: 'rgba(0,0,0,0.03)',
                resize: 'vertical',
              }}
            />
          ) : (
            <input
              className="dv-br-q__textInput"
              type="text"
              placeholder={placeholder}
              value={draft}
              onChange={(e) => setDraft(String(e?.target?.value ?? ''))}
              onFocus={() => {
                isFocusedRef.current = true;
              }}
              onBlur={(e) => {
                isFocusedRef.current = false;
                commitDraft(String(e?.target?.value ?? ''));
              }}
              style={{
                width: '100%',
                borderRadius: 14,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.18)',
                background: 'rgba(0,0,0,0.03)',
              }}
            />
          )}
        </div>

        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 12 }}>
          Tip: your answer is saved when you leave the field.
        </p>
      </div>
    );
  }

  const selectedCount = selectedKeys.length;

  return (
    <div data-explanation={explanationMode ? 'on' : 'off'}>
      <p className="dv-br-q__question">
        {title}
        <button
          type="button"
          title="toggle explanation on/off"
          id="toggle-explanation"
          onClick={toggleExplanationMode}
        >
          ?
        </button>
        {explainQuestion && <span className="question-explanation">{explainQuestion}</span>}
      </p>

      {instruction ? <p className="dv-br-card__text dv-br-card__muted">{instruction}</p> : null}

      <div className="dv-br-q__options" style={{ marginTop: 16 }}>
        {choices.map((c) => {
          const key = c.id;
          const active = isSelected(key);

          return (
            <button
              key={key}
              type="button"
              className="dv-br-q__optionBtn"
              aria-pressed={active}
              onClick={() => toggleChoice(key)}
              style={{
                borderColor: active ? 'rgba(11,58,82,0.55)' : 'rgba(0,0,0,0.18)',
                background: active ? 'rgba(11,58,82,0.08)' : 'rgba(0,0,0,0.03)',
              }}
            >
              {c.label}
              {c.explanation ? <span className="option-explanation">{c.explanation}</span> : null}
            </button>
          );
        })}
      </div>

      {type === 'multi' ? (
        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 16 }}>
          Selected: <strong>{selectedCount}</strong> — min {min} / max {max}
        </p>
      ) : null}
    </div>
  );
}
